/**
 * Multi-Track Positioning System for Release Timeline
 *
 * Implements intelligent distribution of releases across variable number of horizontal tracks
 * within each swimlane based on product category to prevent overlapping while maintaining chronological order.
 *
 * Track Allocation by Product Category:
 * - Software: 3 tracks (high release volume)
 * - Cloud: 3 tracks (high release volume)
 * - Azure: 1 track (appropriate for release frequency)
 * - OSS: 1 track (appropriate for release frequency)
 */

import { CategorizedRelease, ReleaseCategory } from '../types/release'

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export interface MultiTrackPosition {
  /** Horizontal position (pixels from left) */
  left: number
  /** Card width in pixels */
  width: number
  /** Track number (0-based index) */
  track: number
  /** Vertical position within swimlane */
  top: number
}

export interface PositionedRelease {
  release: CategorizedRelease
  position: MultiTrackPosition
}

export interface MultiTrackConfig {
  /** Height of each track in pixels */
  trackHeight: number
  /** Vertical spacing between tracks */
  trackSpacing: number
  /** Minimum horizontal gap between cards */
  minCardGap: number
  /** Minimum card width */
  minCardWidth: number
  /** Timeline scale (pixels per day) */
  timelineScale: number
  /** Card height */
  cardHeight: number
  /** Number of tracks for this product category */
  trackCount: number
}

interface TrackOccupancy {
  /** Track number (0-based index) */
  trackNumber: number
  /** Array of occupied horizontal ranges [start, end] */
  occupiedRanges: Array<[number, number]>
}

// ============================================================================
// PRODUCT CATEGORY TRACK ALLOCATION
// ============================================================================

/**
 * Track allocation configuration per product category
 */
export const PRODUCT_TRACK_ALLOCATION = {
  [ReleaseCategory.SOFTWARE]: 3, // 3 tracks - use intelligent same-date handling for conflicts
  [ReleaseCategory.CLOUD]: 4,    // Increased to 4 tracks for higher capacity
  [ReleaseCategory.AZURE]: 1,    // Appropriate for release frequency
  [ReleaseCategory.OSS]: 1       // Appropriate for release frequency
} as const

/**
 * Get number of tracks for a product category
 */
export function getTrackCountForCategory(category: ReleaseCategory): number {
  return PRODUCT_TRACK_ALLOCATION[category] || 1
}

// ============================================================================
// DEFAULT CONFIGURATION
// ============================================================================

export const DEFAULT_MULTI_TRACK_CONFIG: Omit<MultiTrackConfig, 'trackCount'> = {
  trackHeight: 55, // Height for each track (card + padding)
  trackSpacing: 5, // Reduced space between tracks for more compact layout
  minCardGap: 4, // Further reduced minimum gap between cards for maximum space utilization
  minCardWidth: 50, // Further reduced minimum card width for optimal space efficiency
  timelineScale: 2, // Will be calculated dynamically
  cardHeight: 45 // Current compact card height
}

// ============================================================================
// TEXT WIDTH CALCULATION UTILITIES
// ============================================================================

/**
 * Calculate optimized text width for timeline bars
 * Uses improved character-based estimation matching actual UI font metrics
 */
export function calculateTextWidth(text: string, fontSize: number = 12): number {
  // Optimized character width approximations based on Material-UI default font (Roboto)
  // These values are calibrated to match actual rendered text width more accurately
  const charWidths = {
    narrow: 0.45,  // i, l, t, f, j, I, 1
    normal: 0.65,  // most characters, numbers
    wide: 0.85,    // m, w, M, W
    space: 0.35,   // space character
    underscore: 0.55, // underscore (common in release names)
    digit: 0.6     // numbers 0-9
  }

  let totalWidth = 0
  for (const char of text) {
    if (char === ' ') {
      totalWidth += charWidths.space
    } else if (char === '_') {
      totalWidth += charWidths.underscore
    } else if (['i', 'l', 't', 'f', 'j', 'I', '1'].includes(char)) {
      totalWidth += charWidths.narrow
    } else if (['m', 'w', 'M', 'W'].includes(char)) {
      totalWidth += charWidths.wide
    } else if (/\d/.test(char)) {
      totalWidth += charWidths.digit
    } else {
      totalWidth += charWidths.normal
    }
  }

  return totalWidth * fontSize
}

/**
 * Smart text wrapping logic for 2-line display
 * Attempts to break text at natural boundaries for optimal readability
 */
export function calculateOptimalTextLayout(text: string, fontSize: number = 11): {
  displayLines: string[]
  maxLineWidth: number
  canFitInTwoLines: boolean
} {
  if (text.length <= 12) {
    // Short text: single line, no wrapping needed
    return {
      displayLines: [text],
      maxLineWidth: calculateTextWidth(text, fontSize),
      canFitInTwoLines: true
    }
  }

  // Find natural break points (spaces, underscores, hyphens)
  const breakPoints = []
  for (let i = 0; i < text.length; i++) {
    if (['_', '-', ' '].includes(text[i])) {
      breakPoints.push(i + 1) // Break after the separator
    }
  }

  // Try different break points to find optimal 2-line layout
  let bestLayout = null
  let bestBalance = Infinity

  for (const breakPoint of breakPoints) {
    if (breakPoint > 4 && breakPoint < text.length - 2) { // Avoid very short lines
      const line1 = text.substring(0, breakPoint).trim()
      const line2 = text.substring(breakPoint).trim()

      const line1Width = calculateTextWidth(line1, fontSize)
      const line2Width = calculateTextWidth(line2, fontSize)
      const maxWidth = Math.max(line1Width, line2Width)

      // Calculate balance score (prefer more even line lengths)
      const balance = Math.abs(line1Width - line2Width)

      if (balance < bestBalance && line1.length >= 3 && line2.length >= 3) {
        bestBalance = balance
        bestLayout = {
          displayLines: [line1, line2],
          maxLineWidth: maxWidth,
          canFitInTwoLines: true
        }
      }
    }
  }

  // If no good break point found, try splitting roughly in the middle
  if (!bestLayout) {
    const midPoint = Math.floor(text.length / 2)
    // Find nearest break character around midpoint
    let splitPoint = midPoint
    for (let offset = 0; offset <= 3; offset++) {
      if (midPoint + offset < text.length && ['_', '-', ' '].includes(text[midPoint + offset])) {
        splitPoint = midPoint + offset + 1
        break
      }
      if (midPoint - offset >= 0 && ['_', '-', ' '].includes(text[midPoint - offset])) {
        splitPoint = midPoint - offset + 1
        break
      }
    }

    const line1 = text.substring(0, splitPoint).trim()
    const line2 = text.substring(splitPoint).trim()

    if (line1.length >= 3 && line2.length >= 3) {
      const line1Width = calculateTextWidth(line1, fontSize)
      const line2Width = calculateTextWidth(line2, fontSize)

      bestLayout = {
        displayLines: [line1, line2],
        maxLineWidth: Math.max(line1Width, line2Width),
        canFitInTwoLines: true
      }
    }
  }

  // Fallback: single line with ellipsis if no good 2-line layout found
  if (!bestLayout) {
    const truncatedText = text.substring(0, 12) + '...'
    return {
      displayLines: [truncatedText],
      maxLineWidth: calculateTextWidth(truncatedText, fontSize),
      canFitInTwoLines: false
    }
  }

  return bestLayout
}

// ============================================================================
// CORE POSITIONING FUNCTIONS
// ============================================================================

/**
 * Calculate multi-track positions for releases within a swimlane
 */
export function calculateMultiTrackPositions(
  releases: CategorizedRelease[],
  dateRange: { start: Date; end: Date },
  productCategory: ReleaseCategory,
  baseConfig: Omit<MultiTrackConfig, 'trackCount'> = DEFAULT_MULTI_TRACK_CONFIG,
  timelineWidth?: number
): PositionedRelease[] {
  // Get track count for this product category
  const trackCount = getTrackCountForCategory(productCategory)

  // Create full config with track count
  const config: MultiTrackConfig = {
    ...baseConfig,
    trackCount
  }

  // Filter and sort releases by date
  const validReleases = releases
    .filter(release => {
      if (!release.releaseDate) {
        // Debug: Log releases with null dates (archived/unreleased)
        if (release.name.toLowerCase().includes('k8s')) {
          console.log(`DEBUG: Filtering out k8s release with null date (archived): ${release.name}`)
        }
        return false
      }

      const releaseDate = new Date(release.releaseDate)
      const isValidDate = !isNaN(releaseDate.getTime())
      const isInRange = releaseDate >= dateRange.start && releaseDate <= dateRange.end

      if (!isValidDate && release.name.toLowerCase().includes('k8s')) {
        console.log(`DEBUG: Filtering out k8s release with invalid date: ${release.name} (${release.releaseDate})`)
      }
      if (isValidDate && !isInRange && release.name.toLowerCase().includes('k8s')) {
        console.log(`DEBUG: Filtering out k8s release outside date range: ${release.name} (${release.releaseDate}) - Range: ${dateRange.start.toISOString().split('T')[0]} to ${dateRange.end.toISOString().split('T')[0]}`)
      }

      return isValidDate && isInRange
    })
    .sort((a, b) => new Date(a.releaseDate!).getTime() - new Date(b.releaseDate!).getTime())

  // Debug: Log which k8s releases made it through filtering
  const k8sValidReleases = validReleases.filter(r => r.name.toLowerCase().includes('k8s'))
  if (k8sValidReleases.length > 0) {
    console.log(`DEBUG: Valid k8s releases for positioning:`, k8sValidReleases.map(r => r.name))
  }

  // ============================================================================
  // INTELLIGENT DATE-BASED TRACK PRE-ASSIGNMENT
  // ============================================================================

  console.log(`DEBUG: Starting intelligent track pre-assignment for ${productCategory} category: ${validReleases.length} releases, ${trackCount} tracks available`)

  // Group releases by date for optimal track distribution
  const releasesByDate = new Map<string, CategorizedRelease[]>()
  validReleases.forEach(release => {
    const dateKey = release.releaseDate!
    if (!releasesByDate.has(dateKey)) {
      releasesByDate.set(dateKey, [])
    }
    releasesByDate.get(dateKey)!.push(release)
  })

  console.log(`DEBUG: [${productCategory}] Grouped releases by ${releasesByDate.size} unique dates:`)
  releasesByDate.forEach((releases, date) => {
    console.log(`  ${date}: ${releases.length} releases`, releases.map(r => r.name))
  })

  // Pre-assign tracks for same-date releases to ensure optimal distribution
  const preAssignedTracks = new Map<string, number>()
  let nextAvailableTrack = 0

  // Sort dates chronologically for better visual flow
  const sortedDates = Array.from(releasesByDate.keys()).sort()
  console.log(`DEBUG: [${productCategory}] Processing dates in order:`, sortedDates)

  sortedDates.forEach(dateKey => {
    const releasesOnDate = releasesByDate.get(dateKey)!
    console.log(`DEBUG: [${productCategory}] Processing ${releasesOnDate.length} releases for date ${dateKey}`)

    // For same-date releases, distribute across different tracks (category-specific)
    releasesOnDate.forEach((release, index) => {
      const assignedTrack = (nextAvailableTrack + index) % trackCount
      preAssignedTracks.set(release.id, assignedTrack)

      // Debug: Log pre-assignment
      console.log(`DEBUG: [${productCategory}] Pre-assigned ${release.name} to track ${assignedTrack} (index=${index}, nextAvailable=${nextAvailableTrack}, maxTrack=${trackCount-1})`)
    })

    // Update next available track to prevent clustering (within category bounds)
    nextAvailableTrack = (nextAvailableTrack + releasesOnDate.length) % trackCount
    console.log(`DEBUG: [${productCategory}] Updated nextAvailableTrack to ${nextAvailableTrack}`)
  })

  console.log(`DEBUG: [${productCategory}] Pre-assignment complete. Total assignments: ${preAssignedTracks.size}`)

  // Initialize track occupancy for the specified number of tracks
  const tracks: TrackOccupancy[] = Array.from({ length: trackCount }, (_, index) => ({
    trackNumber: index,
    occupiedRanges: []
  }))

  const positionedReleases: PositionedRelease[] = []

  // Process each release with intelligent track assignment
  for (let i = 0; i < validReleases.length; i++) {
    const release = validReleases[i]
    let horizontalPosition = calculateHorizontalPosition(release, dateRange, config, timelineWidth)

    // Enhanced same-date handling with intelligent micro-offsets
    if (i > 0) {
      const currentDate = new Date(release.releaseDate!)

      // Count how many previous releases have the same date
      let sameDateCount = 0
      for (let j = 0; j < i; j++) {
        const prevDate = new Date(validReleases[j].releaseDate!)
        if (prevDate.getTime() === currentDate.getTime()) {
          sameDateCount++
        }
      }

      // Apply minimal micro-offsets for same-date releases (reduced due to intelligent track distribution)
      if (sameDateCount > 0) {
        // Reduced offset since intelligent track distribution minimizes clustering
        const baseOffset = 4 // Minimal offset for visual separation
        const progressiveOffset = sameDateCount * baseOffset

        horizontalPosition.left += progressiveOffset

        // Debug: Log same-date handling
        if (release.name.toLowerCase().includes('k8s')) {
          console.log(`DEBUG: Minimal same-date offset for ${release.name}: +${progressiveOffset}px (${sameDateCount} previous same-date releases)`)
        }
      }
    }

    // FORCED TRACK ASSIGNMENT: Use pre-assigned track without validation for same-date releases
    let assignedTrack = preAssignedTracks.get(release.id)

    // Debug track assignment for k8s releases only to reduce noise
    if (release.name.toLowerCase().includes('k8s')) {
      console.log(`DEBUG: [${productCategory}] FORCED track assignment for ${release.name} (ID: ${release.id}):`)
      console.log(`  Pre-assigned track: ${assignedTrack} (valid range: 0-${trackCount-1})`)
    }

    if (assignedTrack === undefined) {
      // Fallback to intelligent track finding if no pre-assignment
      assignedTrack = findAvailableTrack(horizontalPosition, tracks, config)
      if (release.name.toLowerCase().includes('k8s')) {
        console.log(`  No pre-assignment found, using fallback track: ${assignedTrack}`)
      }
    } else {
      // Validate track is within bounds for this category
      if (assignedTrack >= trackCount) {
        console.error(`ERROR: [${productCategory}] Invalid track ${assignedTrack} for ${release.name}! Max track: ${trackCount-1}`)
        assignedTrack = assignedTrack % trackCount
        console.log(`  Corrected to track: ${assignedTrack}`)
      }

      // For same-date releases, FORCE the pre-assigned track to ensure separation
      // Skip availability check to prevent clustering
      if (release.name.toLowerCase().includes('k8s')) {
        console.log(`  🚀 FORCING pre-assigned track ${assignedTrack} (skipping availability check for same-date separation)`)
      }
    }

    // Calculate vertical position within swimlane
    const verticalPosition = calculateVerticalPosition(assignedTrack, config)

    // Create positioned release
    const positionedRelease: PositionedRelease = {
      release,
      position: {
        left: horizontalPosition.left,
        width: horizontalPosition.width,
        track: assignedTrack,
        top: verticalPosition
      }
    }

    // Debug: Log positioning for k8s releases
    if (release.name.toLowerCase().includes('k8s')) {
      console.log(`DEBUG: Positioned ${release.name} at left=${horizontalPosition.left.toFixed(1)}, width=${horizontalPosition.width.toFixed(1)}, track=${assignedTrack}, top=${verticalPosition}`)
    }

    positionedReleases.push(positionedRelease)

    // Update track occupancy
    updateTrackOccupancy(tracks, assignedTrack, horizontalPosition, config)
  }

  // Post-process for optimal distribution if we have multiple tracks
  if (trackCount > 1) {
    return optimizeTrackDistribution(positionedReleases, tracks, config)
  }

  return positionedReleases
}

/**
 * Calculate horizontal position based on release date and duration
 * Uses the same proportional positioning as TimelineScale for consistency
 */
function calculateHorizontalPosition(
  release: CategorizedRelease,
  dateRange: { start: Date; end: Date },
  config: MultiTrackConfig,
  timelineWidth?: number
): { left: number; width: number } {
  // Calculate total timeline duration in days
  const totalDays = Math.ceil(
    (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
  )

  // Use proportional positioning like TimelineScale (not fixed scale)
  // This ensures releases align with the timeline header
  const actualTimelineWidth = timelineWidth || (totalDays * config.timelineScale)

  const releaseDate = new Date(release.releaseDate!)

  // Calculate days since start
  const daysSinceStart = Math.ceil(
    (releaseDate.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
  )

  const left = (daysSinceStart / totalDays) * actualTimelineWidth

  // Smart width calculation with intelligent 2-line text wrapping
  const nameLength = release.name?.length || 0
  const releaseName = release.name || ''

  let width: number

  if (nameLength <= 12) {
    // For short names (≤ 12 characters): use full text, auto-size to content
    const textWidth = calculateTextWidth(releaseName, 11)
    width = textWidth + 16 // Add 16px padding (8px each side)

    // Ensure minimum width for very short names
    width = Math.max(width, 45)

  } else {
    // For long names (> 12 characters): use smart 2-line layout calculation
    const textLayout = calculateOptimalTextLayout(releaseName, 11)
    width = textLayout.maxLineWidth + 16 // Add 16px padding (8px each side)

    // Ensure minimum width for multi-line or truncated text
    width = Math.max(width, 60)
  }

  return { left, width }
}

/**
 * Find the optimal track for a release using intelligent distribution algorithm
 */
function findAvailableTrack(
  horizontalPosition: { left: number; width: number },
  tracks: TrackOccupancy[],
  config: MultiTrackConfig
): number {
  const cardStart = horizontalPosition.left
  const cardEnd = horizontalPosition.left + horizontalPosition.width + config.minCardGap

  // Find all available tracks
  const availableTracks = tracks
    .map((track, index) => ({ track, index }))
    .filter(({ track }) => isTrackAvailable(track, cardStart, cardEnd))

  // If no tracks are available, use overflow strategy
  if (availableTracks.length === 0) {
    return findLeastCongestedTrack(tracks, cardStart, cardEnd)
  }

  // If only one track is available, use it
  if (availableTracks.length === 1) {
    return availableTracks[0].index
  }

  // Multiple tracks available - use intelligent selection
  return selectOptimalTrack(availableTracks, tracks, cardStart, cardEnd, config)
}

/**
 * Check if a track has space for a card in the given range
 */
function isTrackAvailable(
  track: TrackOccupancy,
  cardStart: number,
  cardEnd: number
): boolean {
  for (const [rangeStart, rangeEnd] of track.occupiedRanges) {
    if (cardStart < rangeEnd && cardEnd > rangeStart) {
      return false // Overlap detected
    }
  }
  return true
}

/**
 * Select the optimal track from available options using intelligent distribution
 */
function selectOptimalTrack(
  availableTracks: Array<{ track: TrackOccupancy; index: number }>,
  allTracks: TrackOccupancy[],
  cardStart: number,
  cardEnd: number,
  config: MultiTrackConfig
): number {
  // Calculate scores for each available track
  const trackScores = availableTracks.map(({ track, index }) => {
    const score = calculateTrackScore(track, index, allTracks, cardStart, cardEnd, config)
    return { index, score }
  })

  // Sort by score (higher is better) and return the best track
  trackScores.sort((a, b) => b.score - a.score)
  return trackScores[0].index
}

/**
 * Calculate a score for track assignment (higher score = better choice)
 */
function calculateTrackScore(
  track: TrackOccupancy,
  trackIndex: number,
  allTracks: TrackOccupancy[],
  cardStart: number,
  cardEnd: number,
  config: MultiTrackConfig
): number {
  let score = 0

  // 1. ENHANCED Load balancing: Strongly prefer tracks with fewer releases
  const trackLoad = track.occupiedRanges.length
  const trackLoads = allTracks.map(t => t.occupiedRanges.length)
  const minLoad = Math.min(...trackLoads)
  const maxLoad = Math.max(...trackLoads)

  // Strong preference for tracks with minimum load (50 points)
  // Decreasing score for each additional release on the track
  const loadBalanceScore = trackLoad === minLoad ? 50 :
                          trackLoad === minLoad + 1 ? 30 :
                          trackLoad === minLoad + 2 ? 10 : 0

  // 2. Spacing optimization: Prefer tracks with more breathing room
  const spacingScore = calculateSpacingScore(track, cardStart, cardEnd)

  // 3. REMOVED track preference to eliminate middle track bias
  // This was causing clustering on track 1 (middle track)

  // 4. Continuity bonus: Prefer tracks that maintain visual flow (reduced weight)
  const continuityScore = calculateContinuityScore(track, cardStart, cardEnd) * 0.5

  // 5. NEW: Round-robin bonus to encourage even distribution
  const roundRobinScore = calculateRoundRobinScore(trackIndex, allTracks.length, trackLoads)

  score = loadBalanceScore + spacingScore + continuityScore + roundRobinScore

  return score
}

/**
 * Calculate spacing score based on gaps around the proposed position
 */
function calculateSpacingScore(
  track: TrackOccupancy,
  cardStart: number,
  cardEnd: number
): number {
  let totalGap = 0
  let gapCount = 0

  // Find gaps before and after the proposed position
  const sortedRanges = [...track.occupiedRanges].sort((a, b) => a[0] - b[0])

  for (let i = 0; i < sortedRanges.length; i++) {
    const [rangeStart, rangeEnd] = sortedRanges[i]

    // Gap before this range
    if (cardEnd <= rangeStart) {
      const gap = rangeStart - cardEnd
      totalGap += gap
      gapCount++
    }

    // Gap after this range
    if (cardStart >= rangeEnd) {
      const nextRange = sortedRanges[i + 1]
      const gap = nextRange ? nextRange[0] - cardStart : 200 // Assume 200px if no next range
      totalGap += gap
      gapCount++
    }
  }

  // If no existing ranges, give maximum spacing score
  if (sortedRanges.length === 0) {
    return 50
  }

  // Return average gap size (capped at 50 for scoring purposes)
  return gapCount > 0 ? Math.min(50, totalGap / gapCount / 10) : 0
}

/**
 * Calculate round-robin score to encourage even distribution across all tracks
 */
function calculateRoundRobinScore(trackIndex: number, totalTracks: number, trackLoads: number[]): number {
  if (totalTracks <= 1) {
    return 0
  }

  // Find the track(s) with minimum load
  const minLoad = Math.min(...trackLoads)
  const tracksWithMinLoad = trackLoads.filter(load => load === minLoad).length

  // If this track has minimum load, give it a bonus
  if (trackLoads[trackIndex] === minLoad) {
    // If multiple tracks have min load, prefer lower-indexed tracks for consistent ordering
    const isPreferredMinTrack = trackLoads.slice(0, trackIndex + 1).filter(load => load === minLoad).length === 1
    return isPreferredMinTrack ? 20 : 15
  }

  return 0
}

/**
 * Calculate continuity score to maintain visual flow
 */
function calculateContinuityScore(
  track: TrackOccupancy,
  cardStart: number,
  cardEnd: number
): number {
  // Prefer tracks where this release creates a natural continuation
  const sortedRanges = [...track.occupiedRanges].sort((a, b) => a[0] - b[0])

  for (const [rangeStart, rangeEnd] of sortedRanges) {
    // Small bonus if this release continues a timeline naturally
    if (Math.abs(rangeEnd - cardStart) < 50) { // Within 50px
      return 3
    }
    if (Math.abs(cardEnd - rangeStart) < 50) { // Within 50px
      return 3
    }
  }

  return 0
}

/**
 * Find the least congested track when no tracks are completely available
 */
function findLeastCongestedTrack(
  tracks: TrackOccupancy[],
  cardStart: number,
  cardEnd: number
): number {
  // Calculate congestion score for each track
  const congestionScores = tracks.map((track, index) => {
    const overlapCount = track.occupiedRanges.filter(([rangeStart, rangeEnd]) =>
      cardStart < rangeEnd && cardEnd > rangeStart
    ).length

    const totalOccupancy = track.occupiedRanges.reduce((sum, [start, end]) => sum + (end - start), 0)

    return {
      index,
      score: overlapCount * 100 + totalOccupancy // Prioritize fewer overlaps, then less total occupancy
    }
  })

  // Return track with lowest congestion score
  congestionScores.sort((a, b) => a.score - b.score)
  return congestionScores[0].index
}

/**
 * Calculate vertical position within swimlane based on track number
 */
function calculateVerticalPosition(
  trackNumber: number,
  config: MultiTrackConfig
): number {
  // Track 0 at top with small padding, subsequent tracks below with spacing
  const topPadding = 8 // Add small padding to prevent overlap with swimlane boundary
  return topPadding + trackNumber * (config.trackHeight + config.trackSpacing)
}

/**
 * Update track occupancy with new card position
 */
function updateTrackOccupancy(
  tracks: TrackOccupancy[],
  trackNumber: number,
  horizontalPosition: { left: number; width: number },
  config: MultiTrackConfig
): void {
  const track = tracks[trackNumber]
  const rangeStart = horizontalPosition.left
  const rangeEnd = horizontalPosition.left + horizontalPosition.width + config.minCardGap

  // Add occupied range
  track.occupiedRanges.push([rangeStart, rangeEnd])

  // Sort ranges for efficient overlap checking
  track.occupiedRanges.sort((a, b) => a[0] - b[0])
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Calculate required swimlane height for multi-track layout
 */
export function calculateMultiTrackHeight(
  productCategory: ReleaseCategory,
  baseConfig: Omit<MultiTrackConfig, 'trackCount'> = DEFAULT_MULTI_TRACK_CONFIG
): number {
  const trackCount = getTrackCountForCategory(productCategory)
  const totalTrackHeight = trackCount * baseConfig.trackHeight
  const totalSpacing = Math.max(0, trackCount - 1) * baseConfig.trackSpacing
  const topPadding = 8 // Match the padding added in calculateVerticalPosition
  const bottomPadding = 12 // Reduced bottom padding to better utilize space
  return totalTrackHeight + totalSpacing + topPadding + bottomPadding
}

/**
 * Calculate optimal timeline scale for multi-track layout
 */
export function calculateOptimalScale(
  dateRange: { start: Date; end: Date },
  timelineWidth: number,
  minScale: number = 1,
  maxScale: number = 8
): number {
  const totalDays = Math.ceil(
    (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
  )

  const calculatedScale = timelineWidth / totalDays
  return Math.max(minScale, Math.min(maxScale, calculatedScale))
}

/**
 * Get track distribution statistics for multi-track layout
 */
export function getTrackDistribution(positionedReleases: PositionedRelease[]): {
  trackCounts: Record<number, number>
  totalReleases: number
  maxTrackUsed: number
} {
  const trackCounts: Record<number, number> = {}
  let maxTrackUsed = -1

  positionedReleases.forEach(item => {
    const track = item.position.track
    trackCounts[track] = (trackCounts[track] || 0) + 1
    maxTrackUsed = Math.max(maxTrackUsed, track)
  })

  return {
    trackCounts,
    totalReleases: positionedReleases.length,
    maxTrackUsed
  }
}

/**
 * Create multi-track configuration with custom timeline scale
 */
export function createMultiTrackConfig(
  timelineScale: number,
  productCategory: ReleaseCategory,
  overrides: Partial<Omit<MultiTrackConfig, 'trackCount'>> = {}
): MultiTrackConfig {
  return {
    ...DEFAULT_MULTI_TRACK_CONFIG,
    timelineScale,
    trackCount: getTrackCountForCategory(productCategory),
    ...overrides
  }
}

/**
 * Optimize track distribution for better visual balance
 */
function optimizeTrackDistribution(
  positionedReleases: PositionedRelease[],
  tracks: TrackOccupancy[],
  config: MultiTrackConfig
): PositionedRelease[] {
  // Skip optimization for small datasets
  if (positionedReleases.length < 2 || tracks.length < 2) {
    return positionedReleases
  }

  // Calculate current distribution balance
  const currentDistribution = getTrackDistribution(positionedReleases)
  const trackCounts = Array.from({ length: config.trackCount }, (_, i) => currentDistribution.trackCounts[i] || 0)
  const maxCount = Math.max(...trackCounts)
  const minCount = Math.min(...trackCounts)

  // More aggressive optimization: optimize even for small imbalances
  // This helps achieve better visual distribution
  if (maxCount - minCount === 0) {
    return positionedReleases // Perfect balance already
  }

  // Create optimized copy
  const optimizedReleases = [...positionedReleases]

  // Multiple optimization passes for better results
  let improved = true
  let passCount = 0
  const maxPasses = 3

  while (improved && passCount < maxPasses) {
    improved = false
    passCount++

    // Recalculate distribution for this pass
    const passDistribution = getTrackDistribution(optimizedReleases)
    const passTrackCounts = Array.from({ length: config.trackCount }, (_, i) => passDistribution.trackCounts[i] || 0)
    const passMaxCount = Math.max(...passTrackCounts)
    const passMinCount = Math.min(...passTrackCounts)

    // Try to rebalance by moving releases from overloaded tracks to underloaded tracks
    for (let i = 0; i < optimizedReleases.length; i++) {
      const release = optimizedReleases[i]
      const currentTrack = release.position.track
      const currentTrackCount = passTrackCounts[currentTrack]

      // Skip if this track is not overloaded relative to the minimum
      if (currentTrackCount <= passMinCount + 1) {
        continue
      }

      // Try to find a better track for this release
      const horizontalPos = {
        left: release.position.left,
        width: release.position.width
      }

      // Rebuild track occupancy without this release
      const tempTracks = rebuildTrackOccupancy(
        optimizedReleases.filter((_, idx) => idx !== i),
        config
      )

      // Find the best alternative track
      const newTrack = findAvailableTrack(horizontalPos, tempTracks, config)

      // Move if it improves distribution (more aggressive criteria)
      const newTrackCount = passTrackCounts[newTrack]
      if (newTrack !== currentTrack && newTrackCount < currentTrackCount) {
        // Update the release position
        optimizedReleases[i] = {
          ...release,
          position: {
            ...release.position,
            track: newTrack,
            top: calculateVerticalPosition(newTrack, config)
          }
        }

        // Update distribution tracking
        passTrackCounts[currentTrack]--
        passTrackCounts[newTrack]++
        improved = true
      }
    }
  }

  return optimizedReleases
}

/**
 * Rebuild track occupancy from positioned releases
 */
function rebuildTrackOccupancy(
  positionedReleases: PositionedRelease[],
  config: MultiTrackConfig
): TrackOccupancy[] {
  const tracks: TrackOccupancy[] = Array.from({ length: config.trackCount }, (_, index) => ({
    trackNumber: index,
    occupiedRanges: []
  }))

  positionedReleases.forEach(({ position }) => {
    const track = tracks[position.track]
    const rangeStart = position.left
    const rangeEnd = position.left + position.width + config.minCardGap
    track.occupiedRanges.push([rangeStart, rangeEnd])
  })

  // Sort ranges for each track
  tracks.forEach(track => {
    track.occupiedRanges.sort((a, b) => a[0] - b[0])
  })

  return tracks
}

// ============================================================================
// BACKWARD COMPATIBILITY
// ============================================================================

/**
 * @deprecated Use calculateMultiTrackPositions instead
 */
export const calculateDualTrackPositions = calculateMultiTrackPositions

/**
 * @deprecated Use createMultiTrackConfig instead
 */
export function createDualTrackConfig(
  timelineScale: number,
  overrides: Partial<Omit<MultiTrackConfig, 'trackCount'>> = {}
): MultiTrackConfig {
  return createMultiTrackConfig(timelineScale, ReleaseCategory.SOFTWARE, overrides)
}
